import { Permissions } from "utils/permissions/permissionConstants";

/**
 * Project-related types and interfaces for Trial-aware RBAC
 * Based on the new API endpoints and trial restrictions
 */

// Project Role Enum (from backend)
export enum ProjectRole {
  CREATOR = "creator",
  EDITOR = "editor",
  SCRIPT_EDITOR = "script_editor",
  REVIEWER = "reviewer",
  VIEWER = "viewer",
  TRIAL = "trial", // Special role for trial projects
}

/**
 * Project role → permission strings. MUST mirror backend src/authz/policies/project.policy.ts
 * (string literal keys — do not use [ProjectRole.X] computed keys).
 */
export const ProjectRolePolicy: Record<ProjectRole, string[]> = {
  creator: [
    Permissions.PROJECT_READ,
    Permissions.PROJECT_UPDATE,
    Permissions.PROJECT_SCRIPT_UPDATE,
    Permissions.PROJECT_SCRIPT_LOCK,
    Permissions.PROJECT_TAG_UPDATE,
    Permissions.PROJECT_SHARE_LINK_MANAGE,
    Permissions.COMMENT_CREATE,
    Permissions.COMMENT_RESOLVE,
    Permissions.COMMENT_MODERATE,
    Permissions.PROJECT_EXPORT,
    Permissions.PROJECT_BUDGET_RERUN,
    Permissions.PROJECT_BUDGET_LOCK,
    Permissions.PROJECT_SCHEDULE_RERUN,
    Permissions.PROJECT_DELETE,
    Permissions.PROJECT_ARCHIVE,
    Permissions.PROJECT_VERSION_CREATE,
  ],
  editor: [
    Permissions.PROJECT_READ,
    Permissions.PROJECT_UPDATE,
    Permissions.PROJECT_SCRIPT_UPDATE,
    Permissions.PROJECT_SCRIPT_LOCK,
    Permissions.PROJECT_TAG_UPDATE,
    Permissions.PROJECT_SHARE_LINK_MANAGE,
    Permissions.COMMENT_CREATE,
    Permissions.COMMENT_RESOLVE,
    Permissions.PROJECT_EXPORT,
    Permissions.PROJECT_BUDGET_RERUN,
    Permissions.PROJECT_BUDGET_LOCK,
    Permissions.PROJECT_SCHEDULE_RERUN,
    Permissions.PROJECT_VERSION_CREATE,
    Permissions.PROJECT_ARCHIVE,
  ],
  script_editor: [
    Permissions.PROJECT_READ,
    Permissions.PROJECT_SCRIPT_UPDATE,
    Permissions.PROJECT_TAG_UPDATE,
    Permissions.COMMENT_CREATE,
    Permissions.COMMENT_RESOLVE,
  ],
  reviewer: [Permissions.PROJECT_READ, Permissions.COMMENT_CREATE, Permissions.COMMENT_RESOLVE],
  viewer: [Permissions.PROJECT_READ],
  trial: [
    Permissions.PROJECT_READ,
    Permissions.PROJECT_UPDATE,
    Permissions.PROJECT_SCRIPT_UPDATE,
    Permissions.PROJECT_TAG_UPDATE,
    Permissions.PROJECT_SHARE_LINK_MANAGE,
    Permissions.COMMENT_CREATE,
    Permissions.COMMENT_RESOLVE,
    Permissions.PROJECT_ARCHIVE,
    Permissions.PROJECT_DELETE,
  ],
};

// Project Permissions - Array of permission strings (WorkOS-style)
export type ProjectPermissions = string[];

// Project Info (includes trial status)
export interface ProjectInfo {
  isTrialProject: boolean;
  trialExpiryDate?: string;
  canUpgrade: boolean;
  projectId: string;
  projectName?: string;
  freeLicensePremiumLocked?: boolean;
  /** Present when backend persisted project create flow (blank vs import). */
  createSource?: string;
}

// Project Roles (from API response)
export interface ProjectRoles {
  workspaceRole: string; // "owner", "admin", "member", "viewer"
  projectRole: ProjectRole; // "trial" for trial projects, "creator" for paid
}

// Project Permissions API Response
export interface ProjectPermissionsResponse {
  data: {
    permissions: string[]; // Array of permission strings
    projectInfo: ProjectInfo;
    roles: ProjectRoles;
  };
}

// Transformed Project Permissions (for UI)
export interface TransformedProjectPermissions {
  permissions: ProjectPermissions; // Array of permission strings for includes() pattern
  projectInfo: ProjectInfo;
  roles: ProjectRoles;
  workspaceId: string | null;
  userId: string;
}

// Project Permission Check Result
export interface ProjectPermissionCheckResult {
  hasPermission: boolean;
  isTrialRestricted: boolean;
  reason?: string;
  requiredRole?: ProjectRole;
  requiredPermissions?: (keyof ProjectPermissions)[];
  upgradePath?: {
    action: string;
    message: string;
    requiredPlan?: string;
  };
}

// Project Upgrade Info
export interface ProjectUpgradeInfo {
  message: string;
  action: "upgrade" | "contact_admin" | "request_access";
  requiredPlan?: string;
  currentPlan?: string;
  features?: string[];
  permission?: string;
  requiredRole?: ProjectRole;
  isTrialRestricted?: boolean;
}

// Component interfaces
export interface ProjectPermissionGuardProps {
  projectId?: string | number; // Optional - will be auto-detected from URL if not provided
  permission?: string;
  role?: ProjectRole;
  mode?: "hide" | "disable" | "show" | "tooltip" | "upgrade";
  fallback?: React.ReactNode;
  upgradeMessage?: string;
  upgradeAction?: () => void;
  children: React.ReactNode;
  showUpgradeButton?: boolean;
  upgradeButtonText?: string;
}

export interface ProjectProtectedRouteProps {
  projectId: string | number;
  permission?: string;
  role?: ProjectRole;
  redirectTo?: string;
  children: React.ReactNode;
}

export interface ProjectUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  upgradeInfo: ProjectUpgradeInfo;
  projectInfo: ProjectInfo;
}

// Trial-restricted permissions mapping
export const TRIAL_RESTRICTED_PERMISSIONS: string[] = [
  Permissions.PROJECT_EXPORT,
  Permissions.PROJECT_VERSION_CREATE,
  Permissions.PROJECT_BUDGET_RERUN,
  Permissions.PROJECT_SCHEDULE_RERUN,
];

// Permission to feature mapping for upgrade messages
export const PERMISSION_FEATURE_MAP: Record<string, string> = {
  [Permissions.PROJECT_READ]: "View project",
  [Permissions.PROJECT_UPDATE]: "Edit project settings",
  [Permissions.PROJECT_SCRIPT_UPDATE]: "Edit screenplay",
  [Permissions.PROJECT_TAG_UPDATE]: "Manage tags",
  [Permissions.PROJECT_SHARE_LINK_MANAGE]: "Manage share links",
  [Permissions.PROJECT_DELETE]: "Delete project",
  [Permissions.PROJECT_EXPORT]: "Export project",
  [Permissions.PROJECT_VERSION_CREATE]: "Create versions",
  [Permissions.PROJECT_BUDGET_RERUN]: "Rerun budget analysis",
  [Permissions.PROJECT_SCHEDULE_RERUN]: "Rerun scheduling",
};
